import { useEffect, useState } from 'react';
import * as voiceCatalogue from '../../utils/voiceCatalogue';

/**
 * VoicePicker — `<select>` populated from the session-scoped voice catalogue
 * (`GET /voices`). Each voice in the cache is `{ id, name, language, gender }`
 * — the option `value` is the voice `id` (what we send to the API) while the
 * visible label is the human-friendly `name` (with the language/gender as a
 * trailing hint when present).
 *
 * Props:
 *  - value (string|null) — currently selected voice id; '' means "use system default".
 *  - onChange (function)  — called with the new id on each change.
 *  - disabled (boolean)   — when true, the picker is unconditionally disabled.
 *  - id (string)          — DOM id for the `<select>` (for label `htmlFor`).
 *  - includeSystemDefault — when true (default), renders a sentinel option
 *                           labelled "Use system default (<name>)" mapped to ''.
 *
 * Subscribes to the catalogue cache so a 422 partial refresh re-renders the
 * `<option>` list without a page reload.
 */
function VoicePicker({ value, onChange, disabled = false, id, includeSystemDefault = true }) {
  const [cached, setCached] = useState(voiceCatalogue.getCached());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const update = () => setCached(voiceCatalogue.getCached());
    const unsub = voiceCatalogue.subscribe(update);
    if (!voiceCatalogue.getCached()) {
      setLoading(true);
      voiceCatalogue.get()
        .then(update)
        .catch(() => setError('Could not load voices.'))
        .finally(() => setLoading(false));
    }
    return unsub;
  }, []);

  const voices = (cached && Array.isArray(cached.voices)) ? cached.voices : [];
  const systemDefaultId = cached && cached.system_default_voice ? cached.system_default_voice : '';
  const systemDefaultVoice = voices.find((v) => v.id === systemDefaultId) || null;
  const systemDefaultLabel = systemDefaultVoice
    ? systemDefaultVoice.name
    : systemDefaultId;

  const labelFor = (v) => {
    const hint = [v.language, v.gender].filter(Boolean).join(', ');
    return hint ? `${v.name} (${hint})` : v.name;
  };

  return (
    <div className="flex flex-col gap-1" data-testid="voice-picker">
      <select
        id={id}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || loading}
        className="bg-gray-800 border border-gray-700 text-gray-100 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        {includeSystemDefault && (
          <option value="">
            Use system default{systemDefaultLabel ? ` (${systemDefaultLabel})` : ''}
          </option>
        )}
        {voices.map((v) => (
          <option key={v.id} value={v.id}>{labelFor(v)}</option>
        ))}
      </select>
      {loading && <p className="text-xs text-gray-400">Loading voices…</p>}
      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
}

export default VoicePicker;
